import { useMemo } from 'react'
import Link from '#/components/Link'
import cn from '#/utils/classnames'

type PaginationProps = {
  path: string
  active: number
  pageCount: number
}

const activeClass = 'bg-primary text-white'

function paginate({ current, max }: { current: number, max: number }): (string | number)[] | null {
  if (!current || !max) return null
  const items = []
  items.push(1)

  if (current === 1 && max === 1) return items
  if (current > 4) items.push('...')

  const r = 2,
    r1 = current - r,
    r2 = current + r

  for (let i = r1 > 2 ? r1 : 2; i <= Math.min(max, r2); i++) items.push(i)

  if (r2 + 1 < max) items.push('...')
  if (r2 < max) items.push(max)

  return items
}

const Pagination: React.FC<PaginationProps> = ({ path, active, pageCount }) => {
  const pagination = useMemo(
    () => paginate({ current: active, max: pageCount }),
    [active, pageCount]
  )
  if (!pagination) return null

  return (
    <div className='flex w-full flex-row items-end justify-center text-xs'>
      {active > 1 && (
        <Link
          className='mr-1 flex h-[35px] items-center justify-center rounded-lg border px-2'
          href={`${path}${active - 1 === 1 ? '' : `?page=${active - 1}`}`}
        >
          Prev
        </Link>
      )}
      {
        pagination.map((page, i) =>
          (page === '...' ?
            <div key={`${page}-${i}`} className='mr-1'>...</div> :
            (
              <Link
                key={`${page}-${i}`}
                className={cn('border rounded-lg mr-1 w-[35px] h-[35px] flex items-center justify-center', (active === page ? activeClass : null))}
                href={`${path}${page === 1 ? '' : `?page=${page}`}`}
              >
                {page}
              </Link>
            )))
      }
      {active < pageCount && (
        <Link
          className='mr-1 flex h-[35px] items-center justify-center rounded-lg border px-2'
          href={`${path}?page=${active + 1}`}
        >
          Next
        </Link>
      )}
    </div>
  )
}

export default Pagination
